import { useCallback, useEffect, useMemo, useRef } from 'react';

import cloneDeep from 'lodash-es/cloneDeep';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useMembers from '@/hooks/useMembers';
import { applyFilters } from '@/modules/analytics/redux/actions';
import { selectLogsFiltersPanel } from '@/modules/analytics/redux/selectors';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import {
  AccountAnalytics,
  AccountAnalyticsFilterType,
  AnalyticsType,
  InsightFilter,
  LogsAnalytics,
} from '@/shared/types/analytics';
import { Brain } from '@/shared/types/brain';
import { Desk } from '@/shared/types/desk';
import { Member } from '@/shared/types/member';
import { RootState } from '@/shared/types/state';
import { urlSafeBase64Decode, urlSafeBase64Encode } from '@/util/util';

import { filtersEqual, stableStringifyFilters } from './sessionFilterUtils';

export type AccountFilterFormValue = Omit<
  LogsAnalytics,
  'type' | 'minNumUserMessages' | 'agentIds'
> & {
  type: AccountAnalyticsFilterType | string;
  minNumUserMessages?: number | string;
  brainIds?: string[];
  agentIds?: Array<{ value: string; label: string }>;
  reactions?: boolean | null;
  hasRecording?: boolean | null;
  hasVoicemail?: boolean | null;
};

export interface AccountFilterForm {
  filters: AccountFilterFormValue[];
}

interface PersistenceContext {
  availableChannels?: string[];
  availableDesks?: string[];
  availableBrains?: string[];
}

const resolveAccountFilterType = (filterKey?: string | null): string | undefined => {
  if (!filterKey) {
    return undefined;
  }

  if (filterKey.startsWith('insight-')) {
    return 'insightFilters';
  }

  if (filterKey === 'language-models') {
    return 'languageModelIds';
  }

  return filterKey;
};

export const mapAccountFilterToForm = (filter: AccountAnalytics): AccountFilterFormValue => {
  const result: AccountFilterFormValue = { type: filter.type };

  switch (filter.type) {
    case 'channels':
      result.channels = Array.isArray(filter.channels) ? [...filter.channels] : [];
      break;
    case 'deskIds':
      result.deskIds = Array.isArray(filter.deskIds)
        ? filter.deskIds.map((desk) => desk.value)
        : [];
      break;
    case 'brainIds':
      result.brainIds = Array.isArray(filter.brainIds)
        ? filter.brainIds.map((brain) => brain.value)
        : [];
      break;
    case 'agentIds':
      result.agentIds = Array.isArray(filter.agentIds) ? [...filter.agentIds] : [];
      break;
    case 'languageModelIds':
      // legacy account filters stored string ids already
      result.languageModelIds = Array.isArray(filter.languageModelIds)
        ? [...filter.languageModelIds]
        : [];
      break;
    case 'minNumUserMessages': {
      result.minNumUserMessages =
        filter.minNumUserMessages !== undefined && filter.minNumUserMessages !== null
          ? String(filter.minNumUserMessages)
          : undefined;
      break;
    }
    case 'isTest':
      result.isTest =
        filter.isTest === undefined || filter.isTest === null ? null : Boolean(filter.isTest);
      break;
    case 'insightFilters':
      result.insightFilters = Array.isArray(filter.insightFilters)
        ? cloneDeep(filter.insightFilters)
        : [];
      break;
    case 'reactions':
      result.reactions =
        filter.reactions === undefined ? null : (filter.reactions as boolean | null);
      break;
    case 'hasRecording':
      result.hasRecording =
        filter.hasRecording === undefined || filter.hasRecording === null
          ? null
          : Boolean(filter.hasRecording);
      break;
    case 'hasVoicemail':
      result.hasVoicemail =
        filter.hasVoicemail === undefined || filter.hasVoicemail === null
          ? null
          : Boolean(filter.hasVoicemail);
      break;
    case 'ratings':
      result.ratings = Array.isArray(filter.ratings) ? [...filter.ratings].map(String) : [];
      result.ratingsMode = filter.ratingsMode ?? 'moreThan';
      break;
    case 'csatBrain':
      result.csatBrain = Array.isArray(filter.csatBrain) ? filter.csatBrain.map(Number) : [];
      result.csatBrainMode = filter.csatBrainMode ?? 'moreThan';
      break;
    case 'firstMessageType':
      result.firstMessageType = filter.firstMessageType ?? null;
      break;
    case 'tags':
      result.tags = Array.isArray(filter.tags) ? [...filter.tags] : [];
      if (filter.tagsMode) {
        result.tagsMode = filter.tagsMode;
      }
      break;
    default:
      if (filter[filter.type] !== undefined) {
        result[filter.type] = cloneDeep(filter[filter.type]);
      }
  }

  return result;
};

const normalizeAccountFormFilter = (filter: AccountFilterFormValue): AccountFilterFormValue => {
  const normalized: AccountFilterFormValue = { ...filter, type: filter.type };

  const ensureUniqueStrings = (values?: string[]) =>
    Array.isArray(values) ? Array.from(new Set(values.filter(Boolean))) : [];

  if (normalized.channels) {
    normalized.channels = ensureUniqueStrings(normalized.channels);
  }

  if (normalized.deskIds) {
    normalized.deskIds = ensureUniqueStrings(normalized.deskIds);
  }

  if (normalized.brainIds) {
    normalized.brainIds = ensureUniqueStrings(normalized.brainIds);
  }

  if (normalized.agentIds && Array.isArray(normalized.agentIds)) {
    // AgentIds are objects with { value, label }, deduplicate by value
    const seen = new Set<string>();
    normalized.agentIds = normalized.agentIds.filter((agent) => {
      if (!agent || typeof agent !== 'object' || !agent.value) {
        return false;
      }
      if (seen.has(agent.value)) {
        return false;
      }
      seen.add(agent.value);
      return true;
    });
  }

  if (normalized.languageModelIds) {
    normalized.languageModelIds = ensureUniqueStrings(normalized.languageModelIds);
  }

  if (typeof normalized.minNumUserMessages === 'string') {
    const parsed = parseInt(normalized.minNumUserMessages, 10);
    normalized.minNumUserMessages = Number.isNaN(parsed) ? undefined : parsed;
  }

  if (normalized.insightFilters) {
    normalized.insightFilters = cloneDeep(normalized.insightFilters);
  }

  return normalized;
};

const createFormFiltersFromAccount = (filters: AccountAnalytics[] = []): AccountFilterFormValue[] =>
  filters.map((filter) => normalizeAccountFormFilter(mapAccountFilterToForm(filter)));

export const mapFormFilterToAccount = (
  filter: AccountFilterFormValue,
  desks: Desk[] | undefined,
  brains: Brain[] | undefined,
  members: Member[] | undefined
): AccountAnalytics => {
  const result: AccountAnalytics = { type: filter.type as AccountAnalyticsFilterType };

  switch (filter.type) {
    case 'channels': {
      result.channels = Array.isArray(filter.channels) ? [...filter.channels] : [];
      break;
    }
    case 'deskIds': {
      const mapDesk = (id: string) => {
        const match = desks?.find((desk) => desk.desk_id === id);
        return { value: id, label: match?.name ?? id };
      };
      result.deskIds = Array.isArray(filter.deskIds) ? filter.deskIds.map(mapDesk) : [];
      break;
    }
    case 'brainIds': {
      const mapBrain = (id: string) => {
        const match = brains?.find((brain) => brain.brain_id === id);
        return { value: id, label: match?.name ?? id };
      };
      result.brainIds = Array.isArray(filter.brainIds) ? filter.brainIds.map(mapBrain) : [];
      break;
    }
    case 'agentIds': {
      if (!Array.isArray(filter.agentIds)) {
        result.agentIds = [];
        break;
      }
      // AgentIds can be either { value, label } objects or strings
      result.agentIds = filter.agentIds.map((item) => {
        if (typeof item === 'string') {
          const match = members?.find((member) => member.user_id === item);
          return { value: item, label: match?.name ?? item };
        }
        // Already an object with value and label
        return item;
      });
      break;
    }
    case 'languageModelIds': {
      result.languageModelIds = Array.isArray(filter.languageModelIds)
        ? [...filter.languageModelIds]
        : [];
      break;
    }
    case 'minNumUserMessages': {
      result.minNumUserMessages =
        typeof filter.minNumUserMessages === 'number' ? filter.minNumUserMessages : undefined;
      break;
    }
    case 'isTest': {
      result.isTest =
        filter.isTest === null || filter.isTest === undefined ? undefined : Boolean(filter.isTest);
      break;
    }
    case 'insightFilters': {
      result.insightFilters = Array.isArray(filter.insightFilters)
        ? cloneDeep(filter.insightFilters)
        : [];
      break;
    }
    case 'reactions': {
      if (filter.reactions !== null && filter.reactions !== undefined) {
        result.reactions = Boolean(filter.reactions);
      }
      break;
    }
    case 'hasRecording': {
      if (filter.hasRecording !== null && filter.hasRecording !== undefined) {
        result.hasRecording = Boolean(filter.hasRecording);
      }
      break;
    }
    case 'hasVoicemail': {
      if (filter.hasVoicemail !== null && filter.hasVoicemail !== undefined) {
        result.hasVoicemail = Boolean(filter.hasVoicemail);
      }
      break;
    }
    case 'ratings': {
      result.ratings = Array.isArray(filter.ratings) ? [...filter.ratings] : [];
      result.ratingsMode = filter.ratingsMode ?? 'moreThan';
      break;
    }
    case 'csatBrain': {
      result.csatBrain = Array.isArray(filter.csatBrain) ? [...filter.csatBrain] : [];
      result.csatBrainMode = filter.csatBrainMode ?? 'moreThan';
      break;
    }
    case 'firstMessageType': {
      if (filter.firstMessageType) {
        result.firstMessageType = filter.firstMessageType;
      }
      break;
    }
    case 'tags': {
      result.tags = Array.isArray(filter.tags) ? [...filter.tags] : [];
      if (filter.tagsMode) {
        result.tagsMode = filter.tagsMode;
      }
      break;
    }
    default: {
      if (filter[filter.type] !== undefined) {
        result[filter.type] = cloneDeep(filter[filter.type]);
      }
    }
  }

  return result;
};

const createAccountFiltersFromForm = (
  filters: AccountFilterFormValue[],
  desks: Desk[] | undefined,
  brains: Brain[] | undefined,
  members: Member[] | undefined
): AccountAnalytics[] =>
  filters.map((filter) => mapFormFilterToAccount(filter, desks, brains, members));

const insightFiltersHaveValues = (filters: InsightFilter[]): boolean =>
  filters.some((insight) => {
    if (insight.operator === 'exists') {
      return true;
    }

    if (!insight.values || insight.values.length === 0) {
      return false;
    }

    return insight.values.some((value) => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }

      if (typeof value === 'number') {
        return true;
      }

      if (typeof value === 'boolean') {
        return true;
      }

      return value !== null && value !== undefined;
    });
  });

const shouldPersistAccountFilter = (
  filter: AccountFilterFormValue,
  context: PersistenceContext
): boolean => {
  if (!filter) {
    return false;
  }

  const hasAny = (values?: unknown[]) => Array.isArray(values) && values.length > 0;

  if (filter.type === 'channels') {
    if (!Array.isArray(filter.channels)) {
      return false;
    }

    const available = context.availableChannels ?? [];
    if (available.length > 0) {
      const selection = filter.channels.filter((channel) => available.includes(channel));
      return selection.length > 0;
    }

    return filter.channels.length > 0;
  }

  if (filter.type === 'deskIds') {
    if (!Array.isArray(filter.deskIds)) {
      return false;
    }

    const available = context.availableDesks ?? [];
    if (available.length > 0) {
      const selection = filter.deskIds.filter((deskId) => available.includes(deskId));
      return selection.length > 0;
    }

    return filter.deskIds.length > 0;
  }

  if (filter.type === 'brainIds') {
    if (!Array.isArray(filter.brainIds)) {
      return false;
    }

    const available = context.availableBrains ?? [];
    if (available.length > 0) {
      const selection = filter.brainIds.filter((brainId) => available.includes(brainId));
      return selection.length > 0;
    }

    return filter.brainIds.length > 0;
  }

  if (filter.type === 'agentIds') {
    return hasAny(filter.agentIds);
  }

  if (filter.type === 'languageModelIds') {
    return hasAny(filter.languageModelIds);
  }

  if (filter.type === 'minNumUserMessages') {
    return typeof filter.minNumUserMessages === 'number' && filter.minNumUserMessages >= 1;
  }

  if (filter.type === 'isTest') {
    return filter.isTest !== null && filter.isTest !== undefined;
  }

  if (filter.type === 'reactions') {
    return filter.reactions !== null && filter.reactions !== undefined;
  }

  if (filter.type === 'hasRecording') {
    return filter.hasRecording !== null && filter.hasRecording !== undefined;
  }

  if (filter.type === 'hasVoicemail') {
    return filter.hasVoicemail !== null && filter.hasVoicemail !== undefined;
  }

  if (filter.type === 'firstMessageType') {
    return filter.firstMessageType === 'proactive' || filter.firstMessageType === 'reactive';
  }

  if (filter.type === 'insightFilters' && Array.isArray(filter.insightFilters)) {
    return filter.insightFilters.length > 0 && insightFiltersHaveValues(filter.insightFilters);
  }

  const rawValue = filter[filter.type];

  if (typeof rawValue === 'string') {
    return rawValue.trim().length > 0;
  }

  if (Array.isArray(rawValue)) {
    return rawValue.length > 0;
  }

  return rawValue !== undefined && rawValue !== null;
};

const sanitizeHydratedAccountFilters = (
  filters: AccountFilterFormValue[],
  context: PersistenceContext
): AccountFilterFormValue[] =>
  filters
    .map((rawFilter) => {
      const filter = normalizeAccountFormFilter(rawFilter);

      if (
        filter.type === 'channels' &&
        Array.isArray(filter.channels) &&
        context.availableChannels
      ) {
        const available = new Set(context.availableChannels);
        filter.channels = filter.channels.filter((channel) => available.has(channel));
      }

      if (filter.type === 'deskIds' && Array.isArray(filter.deskIds) && context.availableDesks) {
        const available = new Set(context.availableDesks);
        filter.deskIds = filter.deskIds.filter((deskId) => available.has(deskId));
      }

      if (filter.type === 'brainIds' && Array.isArray(filter.brainIds) && context.availableBrains) {
        const available = new Set(context.availableBrains);
        filter.brainIds = filter.brainIds.filter((brainId) => available.has(brainId));
      }

      return filter;
    })
    .filter((filter) => {
      if (['channels', 'deskIds', 'brainIds'].includes(filter.type)) {
        return shouldPersistAccountFilter(filter, context);
      }
      return true;
    });

const accountFiltersEqual = (
  left: AccountAnalytics[] = [],
  right: AccountAnalytics[] = []
): boolean => {
  const leftForm = createFormFiltersFromAccount(left);
  const rightForm = createFormFiltersFromAccount(right);

  return filtersEqual(
    leftForm as unknown as LogsAnalytics[],
    rightForm as unknown as LogsAnalytics[]
  );
};

/**
 * Hook for managing account-level session filters mirroring the brain logs UX.
 */
export const useAccountSessionFilters = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const accountFilters = useSelector((state: RootState) => state.analytics.accountAnalytics ?? []);
  const filtersLoaded = useSelector((state: RootState) => state.analytics.filtersLoaded);
  const availableChannels = useSelector(selectAvailableChannels);
  const { openFilter, openFilterIndex } = useSelector(selectLogsFiltersPanel);
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { members } = useMembers();

  const formFilters = useMemo(() => createFormFiltersFromAccount(accountFilters), [accountFilters]);

  const formMethods = useForm<AccountFilterForm>({
    defaultValues: { filters: cloneDeep(formFilters) },
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'filters',
  });

  const syncFormFilters = useCallback(
    (filters: AccountFilterFormValue[]) => {
      formMethods.reset({ filters: cloneDeep(filters) });
    },
    [formMethods]
  );

  const addFilter = useCallback(
    (filterType: string) => {
      const dataModelType = filterType as AccountAnalyticsFilterType;

      const existingFilter = formMethods
        .getValues('filters')
        .find((filter) => filter.type === dataModelType);

      if (existingFilter) {
        append(cloneDeep(existingFilter));
        return;
      }

      const base: AccountFilterFormValue = { type: dataModelType };

      switch (dataModelType) {
        case 'channels':
          base.channels = [];
          break;
        case 'deskIds':
          base.deskIds = [];
          break;
        case 'brainIds':
          base.brainIds = [];
          break;
        case 'agentIds':
          base.agentIds = [];
          break;
        case 'languageModelIds':
          base.languageModelIds = [];
          break;
        case 'minNumUserMessages':
          base.minNumUserMessages = undefined;
          break;
        case 'isTest':
          base.isTest = null;
          break;
        case 'insightFilters':
          base.insightFilters = [];
          break;
        case 'reactions':
          base.reactions = null;
          break;
        case 'hasRecording':
          base.hasRecording = null;
          break;
        case 'hasVoicemail':
          base.hasVoicemail = null;
          break;
        case 'ratings':
          base.ratings = [];
          break;
        case 'csatBrain':
          base.csatBrain = [];
          break;
        case 'firstMessageType':
          base.firstMessageType = null;
          break;
        case 'tags':
          // New tags filters match all tags; saved ones without a mode keep matching any.
          base.tagsMode = 'all';
          break;
        default:
          // no-op for types without explicit initialization
          break;
      }

      append(base);
    },
    [append, formMethods]
  );

  const removeFilter = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const isSavingRef = useRef(false);

  const saveFilters = useCallback(() => {
    isSavingRef.current = true;

    const filterValues = formMethods
      .getValues('filters')
      .map((filter) => normalizeAccountFormFilter(filter));

    const availableDesks = Array.isArray(desks) ? desks.map((desk) => desk.desk_id) : undefined;
    const availableBrains = Array.isArray(brains)
      ? brains.map((brain) => brain.brain_id)
      : undefined;

    const filtered = filterValues.filter((filter) =>
      shouldPersistAccountFilter(filter, {
        availableChannels: Array.isArray(availableChannels) ? availableChannels : undefined,
        availableDesks,
        availableBrains,
      })
    );

    const serialized = createAccountFiltersFromForm(filtered, desks, brains, members);

    if (accountFiltersEqual(serialized, accountFilters)) {
      isSavingRef.current = false;
      return null;
    }

    let nextFormFilters: AccountFilterFormValue[] = filtered;

    if (openFilter !== null && openFilterIndex !== null) {
      const draft = filterValues[openFilterIndex];

      if (draft) {
        const resolvedType = resolveAccountFilterType(openFilter) ?? draft.type;
        const targetType = resolvedType ?? draft.type;

        if (targetType && !filtered.some((filter) => filter.type === targetType)) {
          const draftFilter = cloneDeep({ ...draft, type: targetType });
          const insertionIndex = Math.min(openFilterIndex, filtered.length);
          const restored = [...filtered];
          restored.splice(insertionIndex, 0, draftFilter);
          nextFormFilters = restored;
        }
      }
    }

    dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: serialized }));
    syncFormFilters(nextFormFilters);

    setTimeout(() => {
      isSavingRef.current = false;
    }, 50);

    return filtered;
  }, [
    accountFilters,
    availableChannels,
    brains,
    desks,
    dispatch,
    formMethods,
    members,
    syncFormFilters,
    openFilter,
    openFilterIndex,
  ]);

  const lastUrlFiltersRef = useRef<string | null>(null);
  const wroteUrlRef = useRef(false);
  const ensureLoadedOnceRef = useRef(false);
  const filtersRef = useRef<AccountAnalytics[]>(accountFilters);
  const filtersLoadedRef = useRef(filtersLoaded);

  useEffect(() => {
    filtersRef.current = accountFilters;
  }, [accountFilters]);

  useEffect(() => {
    filtersLoadedRef.current = filtersLoaded;
  }, [filtersLoaded]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filters');
    const token = filterParam ?? '__none__';

    if (!filterParam) {
      if (!ensureLoadedOnceRef.current && !filtersLoadedRef.current) {
        dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: filtersRef.current }));
      }
      ensureLoadedOnceRef.current = true;
      return;
    }

    if (wroteUrlRef.current && token === (lastUrlFiltersRef.current ?? '__none__')) {
      return;
    }

    try {
      const decoded = urlSafeBase64Decode(filterParam);
      const parsedFilters = JSON.parse(decoded) as AccountFilterFormValue[];

      const availableDesks = Array.isArray(desks) ? desks.map((desk) => desk.desk_id) : undefined;
      const availableBrains = Array.isArray(brains)
        ? brains.map((brain) => brain.brain_id)
        : undefined;

      const sanitized = sanitizeHydratedAccountFilters(parsedFilters, {
        availableChannels: Array.isArray(availableChannels) ? availableChannels : undefined,
        availableDesks,
        availableBrains,
      });

      const serialized = createAccountFiltersFromForm(sanitized, desks, brains, members);

      if (!accountFiltersEqual(serialized, filtersRef.current)) {
        dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: serialized }));
      }
    } catch {
      // ignore malformed URL tokens
    }
  }, [availableChannels, brains, desks, dispatch, location.search, members]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const current = params.get('filters');

    if (lastUrlFiltersRef.current === null && current !== null) {
      lastUrlFiltersRef.current = current;
      return;
    }

    const encoded =
      formFilters.length > 0
        ? urlSafeBase64Encode(stableStringifyFilters(formFilters as unknown as LogsAnalytics[]))
        : null;

    if ((encoded ?? '') === (current ?? '')) {
      return;
    }

    const next = new URLSearchParams(location.search);
    if (encoded) {
      next.set('filters', encoded);
    } else {
      next.delete('filters');
    }

    navigate({ search: next.toString() }, { replace: true });

    lastUrlFiltersRef.current = encoded ?? '__none__';
    wroteUrlRef.current = true;
  }, [formFilters, location.search, navigate]);

  useEffect(() => {
    if (isSavingRef.current) {
      return;
    }

    const currentFormFilters = formMethods.getValues('filters');
    const filtersToUse = formFilters;

    const hasChanged = !filtersEqual(
      currentFormFilters as unknown as LogsAnalytics[],
      filtersToUse as unknown as LogsAnalytics[]
    );

    if (hasChanged) {
      formMethods.reset({ filters: cloneDeep(filtersToUse) });
    }
  }, [formFilters, formMethods]);

  const activeFilters = accountFilters;
  const filtersForDisplay = formFilters;

  const loadFilters = useCallback(
    (newFilters: AccountFilterFormValue[]) => {
      isSavingRef.current = true;

      const normalized = newFilters.map((filter) => normalizeAccountFormFilter(filter));
      const serialized = createAccountFiltersFromForm(normalized, desks, brains, members);

      dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: serialized }));
      syncFormFilters(normalized);

      setTimeout(() => {
        isSavingRef.current = false;
      }, 50);
    },
    [brains, desks, dispatch, members, syncFormFilters]
  );

  const clearAllFilters = useCallback(() => {
    isSavingRef.current = true;

    dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: [] }));
    syncFormFilters([]);

    setTimeout(() => {
      isSavingRef.current = false;
    }, 50);
  }, [dispatch, syncFormFilters]);

  const removeFilterByType = useCallback(
    (filterType: string) => {
      const existingFilters = filtersRef.current;

      if (filterType.startsWith('insightFilter-')) {
        const insightId = filterType.replace('insightFilter-', '');
        const updated = existingFilters
          .map((filter) => {
            if (filter.type === 'insightFilters' && Array.isArray(filter.insightFilters)) {
              const insightFilters = filter.insightFilters.filter(
                (insight) => insight.id !== insightId
              );

              if (insightFilters.length === 0) {
                return null;
              }

              return { ...filter, insightFilters };
            }

            return filter;
          })
          .filter(Boolean) as AccountAnalytics[];

        if (existingFilters.length === 0 && updated.length === 0) {
          return;
        }

        dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: updated }));
        return;
      }

      const updated = existingFilters.filter((filter) => filter.type !== filterType);

      if (existingFilters.length === 0 && updated.length === 0) {
        return;
      }

      dispatch(applyFilters({ type: AnalyticsType.ACCOUNT, filters: updated }));
    },
    [dispatch]
  );

  return {
    formMethods,
    fields,
    addFilter,
    removeFilter,
    saveFilters,
    activeFilters,
    filtersForDisplay,
    removeFilterByType,
    loadFilters,
    clearAllFilters,
  };
};
